import { IParametersUpdateRequest } from '../../../../model/apm/parameters/request/parameters-update-request';
import { IParametersResponse } from '../../../../model/apm/parameters/response/parameters-response';
import { isBlank, ValidationErrors } from '../../../../utils/validation';
import { normalizeRichText } from '../../components/rich-text-utils';

export type ParametersField = keyof IParametersUpdateRequest;

// Formulário guarda tudo como texto (inputs); a conversão para número/nulo acontece no buildParametersRequest
export type ParametersForm = Record<ParametersField, string>;

// Limites validados pela API (ParametersAppService / HomeAppService)
export const NEWS_QUANTITY_MIN = 1;
export const NEWS_QUANTITY_MAX = 20;
export const NEWS_QUANTITY_DEFAULT = 6;
export const OPERATING_HOURS_MAX = 500;
export const URL_MAX = 500;

// Temas do portal (color-schemes.ts do portal): alguns números compartilham a mesma paleta.
// value é o número gravado ao escolher a paleta; numbers são todos os que o portal mapeia para ela.
export interface ThemeOption {
    value: number;
    numbers: number[];
    name: string;
    primary: string;
    secondary: string;
    accent: string;
}

export const THEME_OPTIONS: ThemeOption[] = [
    { value: 3, numbers: [3, 4], name: 'Azul Institucional', primary: '#1976d2', secondary: '#0d47a1', accent: '#42a5f5' },
    { value: 8, numbers: [8, 9], name: 'Verde Sustentável', primary: '#2e7d32', secondary: '#1b5e20', accent: '#66bb6a' },
    { value: 10, numbers: [10], name: 'Vermelho Executivo', primary: '#c62828', secondary: '#b71c1c', accent: '#e57373' },
    { value: 1, numbers: [1, 2], name: 'Amarelo Energia', primary: '#f9a825', secondary: '#f57f17', accent: '#fdd835' },
    { value: 6, numbers: [6], name: 'Laranja Vibrante', primary: '#ed6c02', secondary: '#e65100', accent: '#ff9800' },
    { value: 7, numbers: [7], name: 'Marrom Clássico', primary: '#5d4037', secondary: '#3e2723', accent: '#8d6e63' },
    { value: 5, numbers: [5], name: 'Cinza Corporativo', primary: '#546e7a', secondary: '#37474f', accent: '#78909c' },
];

// Sem tema definido o portal usa o azul
export const DEFAULT_THEME_OPTION = THEME_OPTIONS[0];

export function findThemeOption(theme?: number | string | null): ThemeOption | undefined {
    const n = Number(theme);
    if (!theme || Number.isNaN(n)) return undefined;
    return THEME_OPTIONS.find((o) => o.numbers.includes(n));
}

export interface ParametersFieldConfig {
    field: ParametersField;
    label: string;
    helperText?: string;
    maxLength?: number;
}

export const CONTENT_FIELDS: ParametersFieldConfig[] = [
    { field: 'institutional', label: 'Institucional', helperText: 'Apresentação da entidade' },
    { field: 'history', label: 'História', helperText: 'História do município' },
    { field: 'hymn', label: 'Hino', helperText: 'Letra do hino municipal' },
    { field: 'economy', label: 'Economia', helperText: 'Principais atividades econômicas' },
    { field: 'geographicInformation', label: 'Informações geográficas', helperText: 'Localização, área, clima, população…' },
];

export const TRANSPARENCY_URL_FIELDS: ParametersFieldConfig[] = [
    { field: 'urlDaily', label: 'Diário Oficial', maxLength: URL_MAX },
    { field: 'urlRevenue', label: 'Receitas', maxLength: URL_MAX },
    { field: 'urlExpenses', label: 'Despesas', maxLength: URL_MAX },
    { field: 'urlRevenueExpense', label: 'Receitas e despesas' },
    { field: 'urlPaycheck', label: 'Contracheque' },
    { field: 'urlNfse', label: 'NFS-e', maxLength: URL_MAX },
];

export const COVID_URL_FIELDS: ParametersFieldConfig[] = [
    { field: 'urlRevenueExpensesCovid19', label: 'Receitas e despesas COVID-19', maxLength: URL_MAX },
    { field: 'urlRevenueCovid19', label: 'Receitas COVID-19', maxLength: URL_MAX },
    { field: 'urlExpensesCovid19', label: 'Despesas COVID-19', maxLength: URL_MAX },
    { field: 'urlExpensesCovid19Payed', label: 'Despesas pagas COVID-19', maxLength: URL_MAX },
];

export const SOCIAL_URL_FIELDS: ParametersFieldConfig[] = [
    { field: 'urlProfileFacebook', label: 'Facebook' },
    { field: 'urlProfileInstagram', label: 'Instagram' },
    { field: 'urlProfileYoutube', label: 'YouTube' },
    { field: 'urlProfileTwitter', label: 'X (Twitter)' },
];

const URL_FIELDS = [...TRANSPARENCY_URL_FIELDS, ...COVID_URL_FIELDS, ...SOCIAL_URL_FIELDS];
const RICH_TEXT_FIELDS = CONTENT_FIELDS.map((c) => c.field);
const NUMBER_FIELDS: ParametersField[] = ['theme', 'newsQuantityHome'];

const ALL_FIELDS: ParametersField[] = [
    ...NUMBER_FIELDS, ...RICH_TEXT_FIELDS, 'operatingHours', ...URL_FIELDS.map((c) => c.field),
];

// Abas do formulário: usadas para indicar onde estão os erros de validação
export const TAB_FIELDS: ParametersField[][] = [
    ['theme', 'newsQuantityHome', 'operatingHours', ...SOCIAL_URL_FIELDS.map((c) => c.field)],
    RICH_TEXT_FIELDS,
    [...TRANSPARENCY_URL_FIELDS, ...COVID_URL_FIELDS].map((c) => c.field),
];

export function toParametersForm(response?: IParametersResponse): ParametersForm {
    return ALL_FIELDS.reduce((form, field) => {
        const value = response?.[field];
        form[field] = value === undefined || value === null ? '' : String(value);
        return form;
    }, {} as ParametersForm);
}

export function isHttpUrl(value: string): boolean {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

function rangeError(value: string, min: number, max: number): string | undefined {
    if (isBlank(value)) return undefined;
    const n = Number(value);
    return Number.isInteger(n) && n >= min && n <= max ? undefined : `Informe um número entre ${min} e ${max}`;
}

export function validateParameters(form: ParametersForm): ValidationErrors<ParametersField> {
    const errors: ValidationErrors<ParametersField> = {
        newsQuantityHome: rangeError(form.newsQuantityHome, NEWS_QUANTITY_MIN, NEWS_QUANTITY_MAX),
        operatingHours: form.operatingHours.trim().length > OPERATING_HOURS_MAX ? `Máximo de ${OPERATING_HOURS_MAX} caracteres` : undefined,
    };
    URL_FIELDS.forEach(({ field, maxLength }) => {
        const value = form[field].trim();
        if (!value) return;
        if (maxLength && value.length > maxLength) errors[field] = `Máximo de ${maxLength} caracteres`;
        else if (!isHttpUrl(value)) errors[field] = 'Informe um endereço completo, começando com http:// ou https://';
    });
    return errors;
}

function toNumber(value: string): number | null {
    return isBlank(value) ? null : Number(value);
}

function toText(value: string): string | null {
    const text = value.trim();
    return text ? text : null;
}

// Monta o PUT completo: a API limpa os campos que não forem enviados
export function buildParametersRequest(form: ParametersForm): IParametersUpdateRequest {
    const request: IParametersUpdateRequest = {};
    NUMBER_FIELDS.forEach((f) => { (request as any)[f] = toNumber(form[f]); });
    RICH_TEXT_FIELDS.forEach((f) => { (request as any)[f] = normalizeRichText(form[f]) || null; });
    request.operatingHours = toText(form.operatingHours);
    URL_FIELDS.forEach(({ field }) => { (request as any)[field] = toText(form[field]); });
    return request;
}
